import {
  getAnalytics,
  Analytics,
  setUserProperties as setUserPropertiesThroughFirebase,
  setUserId as setUserIdThroughFirebase,
  logEvent as logEventThroughFirebase,
} from 'firebase/analytics';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { firebaseApp, isFirebaseConfigured } from '~/app/firebase';
import GoogleAnalyticsConstants from '~/constants/google-analytics-constants';
import { useAnalyticsNative } from '~/domain/native/hooks/use-analytics-native';
import { AppState } from '~/reducers';
import logger from '~/utils/logger/logger';

export interface TriggerGAEventParams {
  /**
   * Like "Button"
   */
  category: string;

  /**
   * Like "Click", "Download", "Expand"
   */
  action: string;

  /**
   * Context - Component name
   */
  concatenatedLabel?: string;

  /**
   * Context - Component name
   */
  label?: string;

  /**
   * Value - Search string, dropdown select
   */
  value?: string;
}

const useAnalytics = () => {
  const onWebView = useSelector((state: AppState) => state.native.onWebView);
  const onWebViewInitialized = useSelector((state: AppState) => state.native.onWebViewInitialized);
  const onBrowser = useMemo(() => onWebView === false, [onWebView]);
  const { logEventThroughNative, setUserIdThroughNative, setUserPropsThroughNative } =
    useAnalyticsNative();
  const analytics = useMemo<Analytics | undefined>(() => {
    if (!onBrowser || !isFirebaseConfigured) {
      return undefined;
    }
    try {
      return getAnalytics(firebaseApp);
    } catch (error) {
      logger.warn('analytics', 'Firebase analytics is unavailable:', error);
      return undefined;
    }
  }, [onBrowser]);
  const setUserId = useCallback(
    (userId: string) => {
      if (onBrowser && analytics) {
        setUserIdThroughFirebase(analytics, userId);
        return;
      }
      if (onWebViewInitialized) {
        setUserIdThroughNative(userId);
      }
    },
    [analytics, onBrowser, onWebViewInitialized, setUserIdThroughNative]
  );

  const logEvent = useCallback(
    (eventName: string, parameters?: { [key: string]: unknown }) => {
      if (onBrowser && analytics) {
        logEventThroughFirebase(analytics, eventName, parameters);
        return;
      }
      if (onWebViewInitialized) {
        logEventThroughNative(eventName, parameters);
      }
    },
    [analytics, logEventThroughNative, onBrowser, onWebViewInitialized]
  );

  const setUserProps = useCallback(
    (name: string, value: string) => {
      if (onBrowser && analytics) {
        setUserPropertiesThroughFirebase(analytics, { [name]: value });
        return;
      }
      if (onWebViewInitialized) {
        setUserPropsThroughNative(name, value);
      }
    },
    [analytics, onBrowser, onWebViewInitialized, setUserPropsThroughNative]
  );

  const triggerGAEvent = useCallback(
    ({ category, action, label, concatenatedLabel, value }: TriggerGAEventParams): void => {
      const dataLayerObj = {
        event_category: category,
        event_action: action,
        event_label: label,
        event_concatenatedlabel: concatenatedLabel,
        event_value: value,
      };
      logEvent(GoogleAnalyticsConstants.events.customFleet, dataLayerObj);
    },
    [logEvent]
  );

  return { setUserId, logEvent, setUserProps, triggerGAEvent };
};
export { useAnalytics };
